import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

/**
 * The home page, rebuilt to the client's reference.
 *
 * Shruthi's brief, after two rounds: "something like justinablakeney.com."
 * The previous hub kept our editorial skin (slogan hero, mono labelled card
 * grid, dense recognition band) and she was not convinced, so this pass takes
 * the reference literally. Measured on the live reference:
 *
 *   1. Her face and name first, with a one sentence bio. Not a slogan.
 *   2. A full width quote band in a warm solid.
 *   3. Small arch topped category tiles in one row, one per thing she does.
 *   4. One feature block.
 *   5. A wall of imagery with almost no copy.
 *
 * The arch matters twice here: it is the shape language of the reference and
 * the client is an architect whose own portrait was arch masked. It is the
 * one motif from her references we can own outright.
 *
 * Reading is optional everywhere below the bio. That is the point.
 */

const rise = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-10% 0px" },
  transition: { duration: 0.9, ease: [0.19, 1, 0.22, 1] },
};

/* 1 · Her, first. */
export const HomeIntro = () => (
  <section
    className="relative bg-cream text-ink pt-[136px] pb-16 md:pb-20"
    data-testid="home-intro"
  >
    <div className="mx-auto max-w-[1600px] px-6 md:px-10 flex flex-col items-center text-center">
      {/* CSS entrance, not framer: this fold is her name and face and must
          render whatever JavaScript does. 340px logical is the sharpness
          ceiling: the source is 720px wide, covering 340 at dpr 2. */}
      <figure className="enter w-[240px] md:w-[300px] aspect-[3/4] overflow-hidden rounded-t-full bg-border2">
        <img
          src="/portfolio/img/shruthi-portrait-crop.jpg"
          alt="Shruthi Ravi"
          width={720}
          height={900}
          fetchPriority="high"
          className="h-full w-full object-cover"
          data-testid="intro-portrait"
        />
      </figure>

      <h1
        className="enter mt-8 font-display text-display-xl text-ink"
        style={{ animationDelay: "0.15s" }}
        data-testid="intro-name"
      >
        Shruthi Ravi
      </h1>

      <p
        className="enter mt-6 max-w-[58ch] font-sans font-light text-lg md:text-xl text-ink2 leading-relaxed"
        style={{ animationDelay: "0.3s" }}
        data-testid="intro-bio"
      >
        Shruthi Ravi is an architect, interior designer and artist working
        between Bangalore, Goa and Austin. She designs homes, restores old
        houses, and runs the places she builds.
      </p>

      <p className="enter mt-6 label text-terracotta2" style={{ animationDelay: "0.45s" }}>
        TEDx speaker · Entrepreneur of the Year 2017
      </p>
    </div>
  </section>
);

/* 2 · The quote band, in the slot the reference gives its press quote.
   Her favourite line, per her own portfolio, which says it profoundly
   resonates with her. Centred: the band was the only left anchored block on an
   otherwise symmetric page, which is why it read as misaligned. The quote is a
   citation and keeps its own punctuation. */
export const StatementBand = () => (
  <section className="relative bg-forest text-cream" data-testid="statement-band">
    <div className="mx-auto max-w-[1600px] px-6 md:px-10 py-16 md:py-20">
      {/* Static, like the reference's press band. And the measure is in px:
          a ch cap set on the blockquote resolves against ITS 16px font, not
          the display type inside it, which wrapped the quote one word per
          line. 880px yields two balanced lines at the desktop size. */}
      {/* display-m with quote leading, not display-l. The masthead token runs
          77px on a 0.95 line height, which is masthead tuning: two lines of a
          quotation set that way nearly touch, and the band swallowed half a
          viewport. */}
      <blockquote className="mx-auto max-w-[640px] text-center">
        <p className="font-display text-display-m leading-[1.3] text-cream [text-wrap:balance]">
          &ldquo;We shape our buildings, and afterwards they shape us.&rdquo;
        </p>
        <footer className="mt-6 label text-gold">
          Winston Churchill · a line she has carried for years
        </footer>
      </blockquote>
    </div>
  </section>
);

/* 3 · Five arches, one per thing she does. */
const TILES = [
  {
    to: "/work",
    name: "Design",
    image: "/portfolio/img/hero-staircase.jpg",
    alt: "Timber staircase at 77 East Villa",
  },
  {
    to: "/stay",
    name: "Stay",
    image: "/portfolio/img/mt-00.jpg",
    alt: "Mae Terra from above",
  },
  {
    to: "/services",
    name: "Work with me",
    image: "/portfolio/img/tv-01.jpg",
    alt: "Tea Villa Cafe interior",
  },
  {
    to: "/about#talk",
    name: "Speaking",
    image: "/portfolio/img/tedx-poster.jpg",
    alt: "Shruthi Ravi on the TEDx stage",
  },
  {
    to: "/about",
    name: "About",
    image: "/portfolio/img/td-01.jpg",
    alt: "The Teal Door Cafe",
  },
];

export const ArchTiles = () => (
  <section
    className="relative bg-cream text-ink py-16 md:py-20"
    data-testid="arch-tiles"
  >
    <div className="mx-auto max-w-[1600px] px-6 md:px-10">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-x-6 md:gap-x-8 gap-y-12">
        {TILES.map((t, i) => (
          <motion.div
            key={t.name}
            {...rise}
            transition={{ ...rise.transition, delay: i * 0.05 }}
          >
            <Link
              to={t.to}
              className="group/tile block text-center"
              data-testid={`tile-${t.name.split(" ")[0].toLowerCase()}`}
              data-cursor="view"
            >
              <div className="aspect-[3/4] overflow-hidden rounded-t-full bg-border2">
                <img
                  src={t.image}
                  alt={t.alt}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-[1200ms] ease-[cubic-bezier(0.19,1,0.22,1)] group-hover/tile:scale-[1.05]"
                />
              </div>
              <h2 className="mt-5 font-display text-display-s text-ink">
                {t.name}
              </h2>
              <span className="mt-1 inline-flex items-center gap-1.5 label text-ink2 transition-colors group-hover/tile:text-terracotta">
                Visit
                <ArrowUpRight
                  size={11}
                  className="transition-transform duration-300 group-hover/tile:rotate-45"
                />
              </span>
            </Link>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

/* 4 · One feature, and it takes bookings. */
export const MaeTerraFeature = () => (
  <section
    className="relative bg-cream text-ink py-16 md:py-24"
    data-testid="mae-terra-feature"
  >
    <div className="mx-auto max-w-[1600px] px-6 md:px-10">
      <div className="grid grid-cols-12 items-center gap-y-10 gap-x-6 md:gap-x-14">
        <motion.div {...rise} className="col-span-12 md:col-span-7">
          <Link
            to="/ventures/mae-terra"
            data-cursor="view"
            aria-label="Mae Terra, the story"
            className="img-frame block aspect-[16/10]"
          >
            <img
              src="/portfolio/img/mt-02.jpg"
              alt="Mae Terra, seen from the ridge"
              loading="lazy"
              width={1600}
              height={783}
            />
          </Link>
        </motion.div>

        <motion.div
          {...rise}
          transition={{ ...rise.transition, delay: 0.08 }}
          className="col-span-12 md:col-span-5 md:col-start-9"
        >
          <p className="label text-terracotta2">Featured stay</p>
          <h2 className="mt-4 font-display text-display-l text-ink">
            Mae Terra, Goa
          </h2>
          <p className="mt-5 font-sans font-light text-base md:text-lg text-ink2 leading-relaxed max-w-[42ch]">
            A restored Goan Portuguese house on an island inside a bird
            sanctuary. Six rooms, one booking at a time.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a
              href="https://jadehospitainment.com/retreat/mae-terra/?utm_source=shruthiravi&utm_medium=portfolio"
              target="_blank"
              rel="noopener"
              data-testid="feature-book-mae-terra"
              data-cursor="hover"
              className="group inline-flex items-center gap-3 bg-ink px-6 py-3.5 label text-cream hover:bg-terracotta transition-colors duration-500"
            >
              Check availability
              <ArrowUpRight
                size={14}
                className="transition-transform duration-300 group-hover:rotate-45"
              />
            </a>
            <Link
              to="/ventures/mae-terra"
              data-testid="feature-story-mae-terra"
              data-cursor="hover"
              className="inline-flex items-center gap-3 border border-ink px-6 py-3.5 label text-ink hover:bg-ink hover:text-cream transition-colors duration-500"
            >
              The story
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  </section>
);

/* 5 · The wall. Look, do not read.
   Six images, not nine, and a uniform grid, not CSS columns. Columns ended at
   three different heights, which read as unfinished; six at one ratio ends
   flush at two across and at three across. */
const WALL = [
  { src: "/portfolio/img/77-01.jpg", to: "/work/77-east-villa", alt: "77 East Villa" },
  { src: "/portfolio/img/mt-05.jpg", to: "/work/mae-terra-goa", alt: "Mae Terra infinity pool" },
  { src: "/portfolio/img/td-03.jpg", to: "/work/teal-door-cafe", alt: "The Teal Door Cafe" },
  { src: "/portfolio/img/tv-02.jpg", to: "/work/tea-villa-cafe", alt: "Tea Villa Cafe" },
  { src: "/portfolio/img/hrbr-02.jpg", to: "/work/hrbr-villa", alt: "HRBR Villa" },
  { src: "/portfolio/img/ea-01.jpg", to: "/work/earth-alive", alt: "Earth Alive" },
];

export const ImageWall = () => (
  <section
    className="relative bg-cream text-ink pb-20 md:pb-28"
    data-testid="image-wall"
  >
    <div className="mx-auto max-w-[1600px] px-6 md:px-10">
      <motion.div {...rise} className="mb-8 flex items-baseline justify-between gap-4">
        <p className="chapter-num">From the projects</p>
        <Link
          to="/work"
          data-cursor="hover"
          data-testid="wall-see-all"
          className="label editorial-link text-ink"
        >
          See them all
        </Link>
      </motion.div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
        {WALL.map((w, i) => (
          <motion.div
            key={w.src}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-6% 0px" }}
            transition={{ duration: 0.8, delay: (i % 3) * 0.05 }}
          >
            <Link
              to={w.to}
              data-cursor="view"
              aria-label={w.alt}
              className="img-frame block aspect-[4/5]"
            >
              <img src={w.src} alt={w.alt} loading="lazy" />
            </Link>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);
